import Button from "@/Components/Button/Button";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";
import useSavePosTerminal from "@/shared/mutations/pos/useSavePosTerminal";
import useGetTerminals from "@/shared/queries/pos/useGetTerminals";
import usePosUserSettingsQuery from "@/shared/queries/pos/usePosUserSettingsQuery";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import ModalTitle from "./ModalTitle";

interface PosTerminalSettingModalProps {
  show?: boolean;
  onExit: () => void;
}

const PosTerminalSettingModal = ({
  onExit,
  ...props
}: PosTerminalSettingModalProps) => {
  const { t } = useTranslation();
  const settingsQuery = usePosUserSettingsQuery();
  const terminalsQuery = useGetTerminals();
  const { saveTerminal, isPending: isSaving } = useSavePosTerminal();
  const [selectedTerminalId, setSelectedTerminalId] = useState<string | null>(
    null
  );

  const settings = settingsQuery.data;
  const terminals = terminalsQuery.data ?? [];
  const currentTerminal = terminals.find(
    ({ pos_terminal_id }) => pos_terminal_id === settings?.default_pos_terminal_id
  );

  useEffect(() => {
    if (settings) {
      setSelectedTerminalId(settings.default_pos_terminal_id ?? null);
    }
  }, [settings?.default_pos_terminal_id]);

  const handleSave = () => {
    if (!selectedTerminalId) return;
    saveTerminal(selectedTerminalId).then(onExit);
  };

  const isLoading = settingsQuery.isPending || terminalsQuery.isPending;

  return (
    <Modal onExit={onExit} width="50rem" {...props}>
      <ModalTitle mb="2rem" p="0">
        {t("kioskPage.managePosTerminal")}
      </ModalTitle>
      {isLoading && <Spinner />}
      {(settingsQuery.isError || terminalsQuery.isError) && (
        <div>{t("kioskPage.posTerminalLoadError")}</div>
      )}
      {settings && terminalsQuery.isSuccess && (
        <div className="flex flex-col gap-6">
          <p className="text-small opacity-70">
            {t("kioskPage.currentPosTerminal")}:{" "}
            <span className="font-semibold">
              {currentTerminal?.pos_terminal_name ?? t("kioskPage.noPosTerminal")}
            </span>
          </p>
          <Select
            label={t("posOrders.posTerminal")}
            placeholder={t("posOrders.selectPosTerminal")}
            value={terminals.find(
                ({ pos_terminal_id }) => pos_terminal_id === selectedTerminalId
              ) ?? null}
            options={terminals}
            getOptionLabel={(option) => option.pos_terminal_name}
            getOptionValue={(option) => option.pos_terminal_id}
            onChange={(v) => setSelectedTerminalId(v?.pos_terminal_id ?? null)}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
            }}
            menuPortalTarget={document.getElementById("root")}
          />
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onExit}>
              {t("cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              isDisabled={
                !selectedTerminalId ||
                selectedTerminalId === settings.default_pos_terminal_id
              }
              isPending={isSaving}
            >
              {t("save")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PosTerminalSettingModal;
